import React, { useState, useRef, useCallback } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { useOrderDetailContext } from 'providers/OrderDetailProvider';

import Table from 'components/common/Table';
import OrderLineExpandedRow from 'components/add_order/order/OrderDetail/OrderLines/OrderLineExpandedRow';
import OrderCancelModal from 'components/add_order/order/OrderDetail/OrderLines/OrderCancelModal';

import { orderApi } from 'api';
import { ORDER_DETAIL_ITEMS_COLUMNS } from 'constants/order';

const OrderLines = ({ orderId, onOrderUpdate }) => {
  const { orderDetailData } = useOrderDetailContext();

  const tableRef = useRef(null);

  const [orderLines, setOrderLines] = useState([]);
  const [showOrderCancelModal, setShowOrderCancelModal] = useState(false);

  const refreshOrderData = useCallback(() => {
    tableRef.current?.refetch();
    onOrderUpdate?.();
  }, [onOrderUpdate]);

  const handleOpenCancelModal = async () => {
    try {
      const response = await orderApi.getOrderLines(orderId);

      setOrderLines(response || []);
      setShowOrderCancelModal(true);
    } catch (error) {
      toast.error('Failed to fetch order data');
    }
  };

  const handleCancelOrders = async selectedLineIds => {
    try {
      await orderApi.cancelOrders(orderId, selectedLineIds);

      toast.success('Successfully canceled orders');
      refreshOrderData();
    } catch (error) {
      toast.error('Failed to cancel orders');
    }
  };

  const handleOrderConnect = async () => {
    try {
      const response = await orderApi.connectOrders(orderId);

      if (response?.order_status === 'unfulfillable') {
        toast.error('Please check the product information');
      } else if (response?.order_status === 'processing') {
        toast.success('Order successfully connected');
        refreshOrderData();
      }
    } catch (error) {
      toast.error('Failed to connect order');
    }
  };

  const headerActions = (
    <>
      {orderDetailData?.orderStatus === 'unfulfillable' && (
        <Button
          variant="success"
          size="sm"
          className="text-white"
          onClick={handleOrderConnect}
        >
          Connect
        </Button>
      )}

      <Button variant="danger" size="sm" onClick={handleOpenCancelModal}>
        Cancel Orders
      </Button>
    </>
  );

  return (
    <>
      <Table
        ref={tableRef}
        title={`Order #${orderDetailData?.channelOrderId} Items`}
        apiService={orderApi}
        columns={ORDER_DETAIL_ITEMS_COLUMNS}
        fetchConfig={{
          method: 'getOrderLines',
          transformResponse: response => ({
            data: response || [],
            totalItems: response?.length || 0,
          }),
        }}
        resourceId={orderId}
        enablePagination={false}
        renderExpandedRow={row => (
          <OrderLineExpandedRow shipmentLine={row.original.shipment_lines} />
        )}
        headerActions={headerActions}
      />

      <OrderCancelModal
        show={showOrderCancelModal}
        onHide={() => setShowOrderCancelModal(false)}
        orderLines={orderLines}
        onConfirm={handleCancelOrders}
      />
    </>
  );
};

export default OrderLines;
